import React from 'react'
import { useTranslation } from 'react-i18next'
import { useInsightContentData } from 'hooks/useInsightModalState'
import { InsightContentSettingType } from 'components/insightContent/types'
import { CountTypeStrings } from 'enums/CountType'
import { getPercentOfTotal, getMeasureTypeByCountType } from 'components/insightContent/utils/utils'
import { PercentDisplay } from 'components/insightContent/visualization/Overview'
import SparklineOverview from 'components/insightContent/visualization/Overview/SparklineOverview'
import { DayHourTimeZone } from 'enums'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import { useDynamicTotalMeasure } from 'components/insightContent/hooks'
import UnexpectedErrorBoundary from 'components/UnexpectedErrorBoundary'
import RunSearchEffect from './RunSearchEffect'
import formatMentionsDisplay from '../utils'

const SingleMeasure = ({
  detailSetting,
  countType,
  children,
  customMentionDisplay,
  customizedInfoDisplay,
  forceNotScale
}: {
  detailSetting: InsightContentSettingType
  countType: CountTypeStrings
  children: React.ReactNode
  customMentionDisplay?: (mentions: number | undefined) => string
  customizedInfoDisplay?: (value: number, displayedCount: string) => React.ReactNode
  forceNotScale?: boolean
}) => {
  const insightContent = useInsightContentData(InsightContentType.NAME_MEASURE_BY)
  const total = useDynamicTotalMeasure(detailSetting)
  const { t } = useTranslation()

  const handleMentionsDisplay = (mentions: number | undefined) => {
    if (customMentionDisplay) {
      return customMentionDisplay(mentions)
    }

    return formatMentionsDisplay(mentions)
  }

  const count = handleMentionsDisplay(
    insightContent?.data?.[0]?.results?.[0]?.data?.metrics?.[0]?.dataset?.[0]?.set?.[0]
  )
  const percent = getPercentOfTotal(
    insightContent?.data?.[0]?.results?.[0]?.data?.metrics?.[0]?.dataset?.[0]?.set?.[0],
    total
  )

  const handleCustomizedInfoDisplay = () => {
    if (customizedInfoDisplay) {
      return customizedInfoDisplay(
        insightContent?.data?.[0]?.results?.[0]?.data?.metrics?.[0]?.dataset?.[0]?.set?.[0],
        count
      )
    }

    if (detailSetting.timeZone === DayHourTimeZone.LOCAL) {
      return (
        <PercentDisplay
          count={count}
          sideLabel={
            percent !== undefined &&
            t('percentOfPublishDateTimeLocal', {
              percent,
              interpolation: { escapeValue: false }
            })
          }
        />
      )
    }

    return undefined
  }

  return (
    <UnexpectedErrorBoundary>
      <RunSearchEffect
        detailSetting={detailSetting}
        countType={countType}
        forceNotScale={forceNotScale}>
        <SparklineOverview
          title={children}
          displayTitleTooltip={detailSetting.displayTitleTooltip}
          titleTooltipContent={t('insightModalOverviewTooltip')}
          count={count}
          sparklineData={
            insightContent?.data?.[0]?.results?.[0]?.data?.metrics?.[1]?.dataset?.[0]?.set
          }
          measureType={getMeasureTypeByCountType(countType)}
          percent={percent}
          status={insightContent?.status}
          customizedInfo={handleCustomizedInfoDisplay()}
        />
      </RunSearchEffect>
    </UnexpectedErrorBoundary>
  )
}
export default SingleMeasure
